import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Box, Text, HStack, Badge, IconButton, Select,
  VStack, Flex, useToast,
} from '@chakra-ui/react';
import { FiX, FiUser } from 'react-icons/fi';
import api from '../api';

/**
 * AssignUserModal
 *
 * entityType: 'project' | 'task' | 'subtask'
 * entityId:   the id
 * entityName: display name (shown in header)
 *
 * Projects and tasks use junction tables (project_users / task_users).
 * Sub-tasks use a single assigned_to FK — only one user, replaced not added.
 */
export default function AssignUserModal({ entityType, entityId, entityName, isOpen, onClose, onSaved }) {
  const [allUsers, setAllUsers]       = useState([]);
  const [assigned, setAssigned]       = useState([]); // [{user_id, display_name}]
  const [saving, setSaving]           = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const toast = useToast();
  const isSubTask = entityType === 'subtask';

  const fetchAllUsers = async () => {
    try { const { data } = await api.get('/calendar/users'); setAllUsers(data); } catch(e) {}
  };

  const fetchAssigned = async () => {
    if (!entityId) return;
    try {
      if (isSubTask) {
        const { data } = await api.get(`/sub-tasks/${entityId}`);
        if (data.assigned_to) {
          const u = allUsers.find(u => u.id === data.assigned_to);
          setAssigned(u ? [{ user_id: u.id, display_name: u.display_name || u.username }] : []);
        } else {
          setAssigned([]);
        }
      } else if (entityType === 'task') {
        const { data } = await api.get(`/tasks/${entityId}/users`);
        setAssigned(data.map(u => ({ user_id: u.user_id || u.id, display_name: u.display_name || u.username })));
      } else {
        const { data } = await api.get(`/projects/${entityId}/users`);
        setAssigned(data.map(u => ({ user_id: u.user_id || u.id, display_name: u.display_name || u.username })));
      }
    } catch(e) { setAssigned([]); }
  };

  useEffect(() => {
    if (isOpen && entityId) {
      fetchAllUsers().then(fetchAssigned);
      setSelectedUserId('');
    }
  }, [isOpen, entityId]); // eslint-disable-line

  // Re-fetch assigned after allUsers loads so subtask name lookup works
  useEffect(() => {
    if (isOpen && allUsers.length > 0 && entityId) fetchAssigned();
  }, [allUsers]); // eslint-disable-line

  const handleAdd = async () => {
    if (!selectedUserId) return;
    setSaving(true);
    try {
      if (isSubTask) {
        // Sub-task: single assigned_to field — replace
        await api.put(`/sub-tasks/${entityId}`, { assigned_to: selectedUserId });
        const u = allUsers.find(u => u.id === selectedUserId);
        setAssigned(u ? [{ user_id: u.id, display_name: u.display_name || u.username }] : []);
      } else if (entityType === 'task') {
        await api.post(`/tasks/${entityId}/users`, { user_id: selectedUserId });
        await fetchAssigned();
      } else {
        await api.post(`/projects/${entityId}/users`, { user_id: selectedUserId });
        await fetchAssigned();
      }
      setSelectedUserId('');
      toast({ title: 'User assigned', status: 'success', duration: 1500 });
      if (onSaved) onSaved();
    } catch(e) {
      toast({ title: 'Failed', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  const handleRemove = async (userId) => {
    setSaving(true);
    try {
      if (isSubTask) {
        await api.put(`/sub-tasks/${entityId}`, { assigned_to: null });
        setAssigned([]);
      } else if (entityType === 'task') {
        await api.delete(`/tasks/${entityId}/users/${userId}`);
        setAssigned(p => p.filter(u => u.user_id !== userId));
      } else {
        await api.delete(`/projects/${entityId}/users/${userId}`);
        setAssigned(p => p.filter(u => u.user_id !== userId));
      }
      if (onSaved) onSaved();
    } catch(e) {
      toast({ title: 'Failed', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  // Users not yet assigned (for the add dropdown)
  const unassigned = allUsers.filter(u => !assigned.find(a => a.user_id === u.id));

  const typeLabel = isSubTask ? 'Sub-task' : entityType === 'task' ? 'Task' : 'Project';
  const typeColor = isSubTask ? 'purple' : entityType === 'task' ? 'blue' : 'teal';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">
          <HStack spacing={2}>
            <FiUser size={15} />
            <Text>Assign User</Text>
          </HStack>
          <HStack spacing={1} mt={1}>
            <Badge colorScheme={typeColor} fontSize="8px">{typeLabel}</Badge>
            <Text fontSize="11px" fontWeight="400" color="gray.600" noOfLines={1}>{entityName}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            {/* Currently assigned */}
            <Box>
              <Text fontSize="9px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>
                Currently Assigned
              </Text>
              {assigned.length === 0 ? (
                <Text fontSize="11px" color="gray.400">No users assigned yet.</Text>
              ) : (
                <Flex gap={1} flexWrap="wrap">
                  {assigned.map(u => (
                    <Badge key={u.user_id} colorScheme={typeColor} fontSize="10px"
                      display="flex" alignItems="center" gap={1} px={2} py={1}>
                      {u.display_name}
                      <IconButton icon={<FiX />} size="xs" variant="ghost" minW="14px" h="14px"
                        color="current" _hover={{ bg: 'whiteAlpha.300' }}
                        onClick={() => handleRemove(u.user_id)} aria-label="Remove" isLoading={saving} />
                    </Badge>
                  ))}
                </Flex>
              )}
            </Box>

            {/* Add user (single-user sub-tasks: replace; multi-user project/task: add) */}
            <Box>
              <Text fontSize="9px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>
                {isSubTask ? 'Assign to User' : 'Add User'}
              </Text>
              <HStack spacing={1}>
                <Select size="sm" flex={1} placeholder="Select user…" value={selectedUserId}
                  onChange={e => setSelectedUserId(e.target.value)}>
                  {(isSubTask ? allUsers : unassigned).map(u => (
                    <option key={u.id} value={u.id}>{u.display_name || u.username}</option>
                  ))}
                </Select>
                <Button size="sm" colorScheme={typeColor} isLoading={saving}
                  isDisabled={!selectedUserId} onClick={handleAdd}>
                  {isSubTask ? 'Set' : 'Add'}
                </Button>
              </HStack>
              {isSubTask && (
                <Text fontSize="9px" color="gray.400" mt={1}>Sub-tasks can only have one assigned user.</Text>
              )}
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" onClick={onClose}>Done</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
